import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import isMobile from "ismobilejs";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export interface BreadcrumbMobileStyles {
    mobileStyle?: InjectableCss;
}

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;

    /* Allow the trail to wrap at item boundaries on narrow screens instead of squeezing each
       link to a sliver and breaking the text mid-word (e.g. "Ho me"). Only wraps when space is
       tight, so the wide desktop layout is unaffected. */
    flex-wrap: wrap;
    row-gap: 4px;
    & a {
        overflow-wrap: break-word;
    }
    ${injectCss}
    & div {
        display: flex;
    }

    & div span {
        margin-right: 5px;
    }

    & div span svg {
        color: #094e48;
        height: 30px;
        width: 30px;
    }

    & .breadcrumb-button,
    .breadcrumb-button:hover {
        background: #fcfcfc;
        border-color: unset;
        padding: unset;
    }
`;

const handleClick = (link: any) => {
    window.location.href = link.href;
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    const [isMobilebroswer, setIsMobile] = useState(false);

    useEffect(() => {
        const handleWindowSize = () => {
            setIsMobile(isMobile().any);
        };
        handleWindowSize();
        const debouncedHandleWindowSize = debounce(handleWindowSize, 200);
        window.addEventListener("resize", debouncedHandleWindowSize);

        return () => {
            window.removeEventListener("resize", debouncedHandleWindowSize);
        };
    }, []);

    if (!links || links.length === 0) {
        return null;
    }
    if (isMobilebroswer) {
        const lastLink = links[links.length - 1];
        const previousLinkHref = links[links.length - 2].href;

        const modifiedLinks = [
            ...links.slice(0, -1), // Copy all elements except the last one
            { ...lastLink, href: previousLinkHref }, // Copy the last link with modified href
        ];

        links = modifiedLinks.slice(-1);
    }

    const children = links.map((link: any, index: number) => {
        const children = link.children;
        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }
        const key = typeof children === "string" ? `${children}_${index}` : index;
        if (index === links.length - 1) {
            return (
                <React.Fragment key={key}>
                    {isMobilebroswer && (
                        <StyledWrapper>
                            <Button className="breadcrumb-button" onClick={() => handleClick(link)}>
                                <ButtonIcon src={ChevronLeft} />
                            </Button>
                            <Link typographyProps={typographyProps} {...link} />
                        </StyledWrapper>
                    )}
                    {!isMobilebroswer && <Link typographyProps={typographyProps} {...link} />}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={key}>
                <Link typographyProps={typographyProps} {...link} />
                <Slash {...typographyProps} />
            </React.Fragment>
        );
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    return (
        <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
            {children}
        </StyledNav>
    );
});

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
